import { ArrowLeft } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link, useParams } from "react-router-dom";

import LayoutAction from "../../actions/LayoutAction";
import { Button, InputField } from "../../components/ui";
import {
  type GtmBrain,
  type GtmBrainDoc,
  type GtmBrainLatestReview,
  type GtmSiteSourceStatus,
  type GtmSiteSourceSummary,
} from "../api/GtmApi";
import {
  useGtmBrainBuildMutation,
  useGtmBrainQuery,
  useGtmBrainSaveMutation,
} from "../hooks/useGtmBrain";
import BrainKnowledgeSidebar from "../brain/BrainKnowledgeSidebar";
import BrainMarketDocView from "../brain/BrainMarketDocView";
import BrainMarketProfilePanel from "../brain/BrainMarketProfilePanel";
import BrainOverviewCanvas from "../brain/BrainOverviewCanvas";
import BrainPersonaDocView from "../brain/BrainPersonaDocView";
import BrainPersonasPanel from "../brain/BrainPersonasPanel";
import BrainCompetitorDocView from "../brain/BrainCompetitorDocView";
import BrainCompetitorsPanel from "../brain/BrainCompetitorsPanel";
import BrainInfluencerDocView from "../brain/BrainInfluencerDocView";
import BrainInfluencersPanel from "../brain/BrainInfluencersPanel";
import BrainObjectionDocView from "../brain/BrainObjectionDocView";
import BrainObjectionsPanel from "../brain/BrainObjectionsPanel";
import BrainPillarDocView from "../brain/BrainPillarDocView";
import BrainPillarsPanel from "../brain/BrainPillarsPanel";
import BrainKeywordsPanel from "../brain/BrainKeywordsPanel";
import BrainSignalDocView from "../brain/BrainSignalDocView";
import BrainSignalsPanel from "../brain/BrainSignalsPanel";
import BrainVoiceDocView from "../brain/BrainVoiceDocView";
import BrainVoicePanel from "../brain/BrainVoicePanel";
import {
  brainToFlow,
  CATEGORY_TO_SECTION,
  type BrainCategoryId,
} from "../brain/brainToFlow";
import GtmBrainEditorPanel, {
  type BrainEditorSectionId,
} from "../brain/GtmBrainEditorPanel";
import { listCompetitors } from "../brain/competitors";
import { listInfluencers } from "../brain/influencers";
import type { MarketProfileDocId } from "../brain/marketProfile";
import { listObjections } from "../brain/objections";
import { listPersonas } from "../brain/personas";
import { listPillars } from "../brain/pillars";
import type { SignalDocId } from "../brain/signalsProfile";
import type { VoiceDocId } from "../brain/voiceProfile";

type ViewMode = "overview" | "editor";

function emptyBrain(): GtmBrain {
  return {
    valueProposition: "",
    businessSummary: "",
    offering: "",
    keyFeatures: "",
    socialProof: "",
    industries: [],
    geographies: [],
    icpSummary: "",
    targetCompanySize: "",
    verticals: [],
    buyingRoles: [],
    championRoles: [],
    userRoles: [],
    companyName: "",
    domain: "",
    linkedinUrl: "",
    companySize: "",
    businessModel: "",
    stage: "",
    language: "",
    toneOfVoice: "",
    voiceLinkedin: "",
    voiceBlog: "",
    signalsJson: "",
    calendarUrl: "",
  };
}

function statusLabel(status?: GtmSiteSourceStatus | null) {
  switch (status) {
    case "gained":
      return "Fetching website…";
    case "suggesting":
      return "Suggesting brain…";
    case "suggested":
      return "Suggested";
    case "error":
      return "Build failed";
    default:
      return "";
  }
}

function isInProgress(status?: GtmSiteSourceStatus | null) {
  return status === "gained" || status === "suggesting";
}

function defaultOpenSections(
  focus?: BrainEditorSectionId | null
): Record<BrainEditorSectionId, boolean> {
  const base: Record<BrainEditorSectionId, boolean> = {
    market: false,
    icp: false,
    company: false,
    voice: false,
    signals: false,
    personas: false,
    pillars: false,
    objections: false,
    competitors: false,
    influencers: false,
    keywords: false,
  };
  if (focus) {
    base[focus] = true;
    if (focus === "market") base.icp = true;
  } else {
    base.market = true;
    base.icp = true;
    base.personas = true;
  }
  return base;
}

export default function GtmBrainPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectId = "" } = useParams<{ projectId: string }>();

  const [websiteUrl, setWebsiteUrl] = useState("");
  const [brain, setBrain] = useState<GtmBrain>(emptyBrain());
  const [docs, setDocs] = useState<GtmBrainDoc[]>([]);
  const [siteSource, setSiteSource] = useState<GtmSiteSourceSummary | null>(null);
  const [latestReview, setLatestReview] = useState<GtmBrainLatestReview | null>(null);
  const [hasBrain, setHasBrain] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("overview");
  const [selectedCategoryId, setSelectedCategoryId] = useState<BrainCategoryId | null>(null);
  const [selectedMarketDocId, setSelectedMarketDocId] = useState<MarketProfileDocId | null>(null);
  const [selectedPersonaKey, setSelectedPersonaKey] = useState<string | null>(null);
  const [selectedPillarKey, setSelectedPillarKey] = useState<string | null>(null);
  const [selectedObjectionKey, setSelectedObjectionKey] = useState<string | null>(null);
  const [selectedCompetitorKey, setSelectedCompetitorKey] = useState<string | null>(null);
  const [selectedInfluencerKey, setSelectedInfluencerKey] = useState<string | null>(null);
  const [selectedVoiceDocId, setSelectedVoiceDocId] = useState<VoiceDocId | null>(null);
  const [selectedSignalDocId, setSelectedSignalDocId] = useState<SignalDocId | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [openSections, setOpenSections] = useState<Record<BrainEditorSectionId, boolean>>(
    defaultOpenSections()
  );

  const brainQuery = useGtmBrainQuery(projectId);
  const buildMutation = useGtmBrainBuildMutation(projectId);
  const saveMutation = useGtmBrainSaveMutation(projectId);

  useEffect(() => {
    document.title = "GTM · Brain";
  }, []);

  useEffect(() => {
    const payload = brainQuery.data;
    if (!payload) return;
    setSiteSource(payload.siteSource);
    setLatestReview(payload.latestReview);
    setDocs(payload.docs || []);
    if (payload.brain) {
      setBrain({ ...emptyBrain(), ...payload.brain });
      setHasBrain(true);
    } else {
      setHasBrain(false);
      setBrain(emptyBrain());
    }
    if (payload.siteSource?.url) {
      setWebsiteUrl(payload.siteSource.url);
    }
  }, [brainQuery.data]);

  useEffect(() => {
    if (!brainQuery.isError) return;
    const err: any = brainQuery.error;
    dispatch(
      LayoutAction.setNotification({
        title: t("Brain"),
        type: "error",
        message: err?.response?.data?.message || err?.message || t("Failed to load brain"),
      }) as any
    );
  }, [brainQuery.error, brainQuery.isError, dispatch, t]);

  const loading = brainQuery.isLoading;
  const saving = saveMutation.isPending;
  const building = buildMutation.isPending;

  const selectedLeafId =
    selectedMarketDocId ||
    selectedPersonaKey ||
    selectedPillarKey ||
    selectedObjectionKey ||
    selectedCompetitorKey ||
    selectedInfluencerKey ||
    selectedVoiceDocId ||
    selectedSignalDocId ||
    null;

  const flow = useMemo(
    () =>
      brainToFlow(brain, docs, {
        selectedCategoryId,
        selectedLeafId,
        query: searchQuery,
      }),
    [brain, docs, selectedCategoryId, selectedLeafId, searchQuery]
  );

  const openEditor = (categoryId: BrainCategoryId, sectionOverride?: BrainEditorSectionId) => {
    const section =
      sectionOverride ||
      ((CATEGORY_TO_SECTION[categoryId] || "market") as BrainEditorSectionId);
    setSelectedCategoryId(categoryId);
    setOpenSections(defaultOpenSections(section));
    setViewMode("editor");
  };

  const clearDocSelections = () => {
    setSelectedMarketDocId(null);
    setSelectedPersonaKey(null);
    setSelectedPillarKey(null);
    setSelectedObjectionKey(null);
    setSelectedCompetitorKey(null);
    setSelectedInfluencerKey(null);
    setSelectedVoiceDocId(null);
    setSelectedSignalDocId(null);
  };

  const openMarketDoc = (docId: MarketProfileDocId) => {
    clearDocSelections();
    setSelectedMarketDocId(docId);
    setSelectedCategoryId("market");
    setViewMode("overview");
  };

  const backToMarketList = () => {
    setSelectedMarketDocId(null);
    setSelectedCategoryId("market");
    setViewMode("overview");
  };

  const openPersona = (key: string) => {
    clearDocSelections();
    setSelectedPersonaKey(key);
    setSelectedCategoryId("personas");
    setViewMode("overview");
  };

  const backToPersonasList = () => {
    setSelectedPersonaKey(null);
    setSelectedCategoryId("personas");
    setViewMode("overview");
  };

  const addPersona = () => {
    const next = [
      ...docs,
      {
        docType: "persona" as const,
        name: "",
        seniority: "",
        description: "",
        topPains: [],
        wateringHoles: [],
      },
    ];
    setDocs(next);
    const personas = listPersonas(next);
    const created = personas[personas.length - 1];
    if (created) openPersona(created.key);
  };

  const discardPersona = () => {
    if (!selectedPersonaKey) {
      backToPersonasList();
      return;
    }
    const item = listPersonas(docs).find((p) => p.key === selectedPersonaKey);
    if (item) {
      setDocs(docs.filter((_, i) => i !== item.index));
    }
    backToPersonasList();
  };

  const openPillar = (key: string) => {
    clearDocSelections();
    setSelectedPillarKey(key);
    setSelectedCategoryId("pillars");
    setViewMode("overview");
  };

  const backToPillarsList = () => {
    setSelectedPillarKey(null);
    setSelectedCategoryId("pillars");
    setViewMode("overview");
  };

  const addPillar = () => {
    const next = [
      ...docs,
      { docType: "pillar" as const, pain: "", valueProposition: "", proof: "" },
    ];
    setDocs(next);
    const pillars = listPillars(next);
    const created = pillars[pillars.length - 1];
    if (created) openPillar(created.key);
  };

  const discardPillar = () => {
    if (!selectedPillarKey) {
      backToPillarsList();
      return;
    }
    const item = listPillars(docs).find((p) => p.key === selectedPillarKey);
    if (item) {
      setDocs(docs.filter((_, i) => i !== item.index));
    }
    backToPillarsList();
  };

  const openObjection = (key: string) => {
    clearDocSelections();
    setSelectedObjectionKey(key);
    setSelectedCategoryId("objections");
    setViewMode("overview");
  };

  const backToObjectionsList = () => {
    setSelectedObjectionKey(null);
    setSelectedCategoryId("objections");
    setViewMode("overview");
  };

  const addObjection = () => {
    const next = [...docs, { docType: "objection" as const, objection: "", response: "" }];
    setDocs(next);
    const objections = listObjections(next);
    const created = objections[objections.length - 1];
    if (created) openObjection(created.key);
  };

  const discardObjection = () => {
    if (!selectedObjectionKey) {
      backToObjectionsList();
      return;
    }
    const item = listObjections(docs).find((o) => o.key === selectedObjectionKey);
    if (item) {
      setDocs(docs.filter((_, i) => i !== item.index));
    }
    backToObjectionsList();
  };

  const openCompetitor = (key: string) => {
    clearDocSelections();
    setSelectedCompetitorKey(key);
    setSelectedCategoryId("competitors");
    setViewMode("overview");
  };

  const backToCompetitorsList = () => {
    setSelectedCompetitorKey(null);
    setSelectedCategoryId("competitors");
    setViewMode("overview");
  };

  const addCompetitor = () => {
    const next = [
      ...docs,
      { docType: "competitor" as const, name: "", domain: "", linkedinUrl: "", description: "", notes: "" },
    ];
    setDocs(next);
    const competitors = listCompetitors(next);
    const created = competitors[competitors.length - 1];
    if (created) openCompetitor(created.key);
  };

  const discardCompetitor = () => {
    if (!selectedCompetitorKey) {
      backToCompetitorsList();
      return;
    }
    const item = listCompetitors(docs).find((c) => c.key === selectedCompetitorKey);
    if (item) {
      setDocs(docs.filter((_, i) => i !== item.index));
    }
    backToCompetitorsList();
  };

  const openInfluencer = (key: string) => {
    clearDocSelections();
    setSelectedInfluencerKey(key);
    setSelectedCategoryId("influencers");
    setViewMode("overview");
  };

  const backToInfluencersList = () => {
    setSelectedInfluencerKey(null);
    setSelectedCategoryId("influencers");
    setViewMode("overview");
  };

  const addInfluencer = () => {
    const next = [
      ...docs,
      { docType: "influencer" as const, name: "", domain: "", linkedinUrl: "", description: "", notes: "" },
    ];
    setDocs(next);
    const influencers = listInfluencers(next);
    const created = influencers[influencers.length - 1];
    if (created) openInfluencer(created.key);
  };

  const discardInfluencer = () => {
    if (!selectedInfluencerKey) {
      backToInfluencersList();
      return;
    }
    const item = listInfluencers(docs).find((i) => i.key === selectedInfluencerKey);
    if (item) {
      setDocs(docs.filter((_, i) => i !== item.index));
    }
    backToInfluencersList();
  };

  const openVoiceDoc = (docId: VoiceDocId) => {
    clearDocSelections();
    setSelectedVoiceDocId(docId);
    setSelectedCategoryId("voice");
    setViewMode("overview");
  };

  const backToVoiceList = () => {
    setSelectedVoiceDocId(null);
    setSelectedCategoryId("voice");
    setViewMode("overview");
  };

  const openSignalDoc = (docId: SignalDocId) => {
    clearDocSelections();
    setSelectedSignalDocId(docId);
    setSelectedCategoryId("signals");
    setViewMode("overview");
  };

  const backToSignalsList = () => {
    setSelectedSignalDocId(null);
    setSelectedCategoryId("signals");
    setViewMode("overview");
  };

  const selectCategory = (id: BrainCategoryId) => {
    clearDocSelections();
    setSelectedCategoryId(id);
    setViewMode("overview");
  };

  const backToOverview = () => {
    clearDocSelections();
    setSelectedCategoryId(null);
    setViewMode("overview");
  };

  const toggleSection = (id: BrainEditorSectionId) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const onBuild = async () => {
    const url = websiteUrl.trim();
    if (!url.startsWith("https://")) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Brain"),
          type: "error",
          message: t("Enter an https:// company website URL"),
        }) as any
      );
      return;
    }
    try {
      await buildMutation.mutateAsync(url);
      dispatch(
        LayoutAction.setNotification({
          title: t("Brain"),
          type: "success",
          message: t("Build started"),
        }) as any
      );
    } catch (err: any) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Brain"),
          type: "error",
          message: err?.response?.data?.message || err?.message || t("Build failed"),
        }) as any
      );
    }
  };

  const onSave = async () => {
    try {
      await saveMutation.mutateAsync({
        brain: {
          valueProposition: brain.valueProposition || "",
          businessSummary: brain.businessSummary || "",
          offering: brain.offering || "",
          keyFeatures: brain.keyFeatures || "",
          socialProof: brain.socialProof || "",
          industries: brain.industries || [],
          geographies: brain.geographies || [],
          icpSummary: brain.icpSummary || "",
          targetCompanySize: brain.targetCompanySize || "",
          verticals: brain.verticals || [],
          buyingRoles: brain.buyingRoles || [],
          championRoles: brain.championRoles || [],
          userRoles: brain.userRoles || [],
          companyName: brain.companyName || "",
          domain: brain.domain || "",
          linkedinUrl: brain.linkedinUrl || "",
          companySize: brain.companySize || "",
          businessModel: brain.businessModel || "",
          stage: brain.stage || "",
          language: brain.language || "",
          toneOfVoice: brain.toneOfVoice || "",
          voiceLinkedin: brain.voiceLinkedin || "",
          voiceBlog: brain.voiceBlog || "",
          signalsJson: brain.signalsJson || "",
          calendarUrl: brain.calendarUrl || "",
        },
        docs: docs.map((d) => ({
          docType: d.docType,
          name: d.name ?? null,
          seniority: d.seniority ?? null,
          description: d.description ?? null,
          topPains: d.topPains || [],
          wateringHoles: d.wateringHoles || [],
          pain: d.pain ?? null,
          valueProposition: d.valueProposition ?? null,
          proof: d.proof ?? null,
          objection: d.objection ?? null,
          response: d.response ?? null,
          domain: d.domain ?? null,
          linkedinUrl: d.linkedinUrl ?? null,
          notes: d.notes ?? null,
          phrase: d.phrase ?? null,
        })),
      });
      dispatch(
        LayoutAction.setNotification({
          title: t("Brain"),
          type: "success",
          message: t("Brain saved"),
        }) as any
      );
    } catch (err: any) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Brain"),
          type: "error",
          message: err?.response?.data?.message || err?.message || t("Save failed"),
        }) as any
      );
    }
  };

  const showOverview = hasBrain && viewMode === "overview" && !isInProgress(siteSource?.status);
  const showEditor = hasBrain && viewMode === "editor";
  const showMarketBrowse =
    showOverview && selectedCategoryId === "market" && !selectedMarketDocId;
  const showMarketDoc =
    showOverview && selectedCategoryId === "market" && !!selectedMarketDocId;
  const showPersonasBrowse =
    showOverview && selectedCategoryId === "personas" && !selectedPersonaKey;
  const showPersonaDoc =
    showOverview && selectedCategoryId === "personas" && !!selectedPersonaKey;
  const showPillarsBrowse =
    showOverview && selectedCategoryId === "pillars" && !selectedPillarKey;
  const showPillarDoc =
    showOverview && selectedCategoryId === "pillars" && !!selectedPillarKey;
  const showObjectionsBrowse =
    showOverview && selectedCategoryId === "objections" && !selectedObjectionKey;
  const showObjectionDoc =
    showOverview && selectedCategoryId === "objections" && !!selectedObjectionKey;
  const showCompetitorsBrowse =
    showOverview && selectedCategoryId === "competitors" && !selectedCompetitorKey;
  const showCompetitorDoc =
    showOverview && selectedCategoryId === "competitors" && !!selectedCompetitorKey;
  const showInfluencersBrowse =
    showOverview && selectedCategoryId === "influencers" && !selectedInfluencerKey;
  const showInfluencerDoc =
    showOverview && selectedCategoryId === "influencers" && !!selectedInfluencerKey;
  const showVoiceBrowse =
    showOverview && selectedCategoryId === "voice" && !selectedVoiceDocId;
  const showVoiceDoc =
    showOverview && selectedCategoryId === "voice" && !!selectedVoiceDocId;
  const showSignalsBrowse =
    showOverview && selectedCategoryId === "signals" && !selectedSignalDocId;
  const showSignalDoc =
    showOverview && selectedCategoryId === "signals" && !!selectedSignalDocId;
  const showKeywordsBrowse = showOverview && selectedCategoryId === "keywords";

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <div className="flex items-start gap-2">
          {showEditor ? (
            <button
              type="button"
              className="mt-0.5 rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
              onClick={() => {
                setViewMode("overview");
                setSelectedCategoryId("market");
              }}
              aria-label={t("Back to overview")}
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          ) : null}
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{t("Brain")}</h1>
            <p className="text-sm text-gray-500">
              {showOverview
                ? t("Explore knowledge categories and open a section to edit.")
                : t("Market profile, ICP, personas, and message pillars for GTM plays.")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasBrain &&
          (showEditor ||
            showPersonasBrowse ||
            showPersonaDoc ||
            showPillarsBrowse ||
            showPillarDoc ||
            showObjectionsBrowse ||
            showObjectionDoc ||
            showCompetitorsBrowse ||
            showCompetitorDoc ||
            showInfluencersBrowse ||
            showInfluencerDoc ||
            showVoiceBrowse ||
            showVoiceDoc ||
            showSignalsBrowse ||
            showSignalDoc ||
            showKeywordsBrowse) ? (
            <Button type="button" variant="secondary" size="sm" isLoading={saving} onClick={onSave}>
              {t("Save")}
            </Button>
          ) : null}
          <Link to={`/workspaces/${projectId}/gtm/leads/plays/new?key=Leads`}>
            <Button type="button" variant="primary" size="sm">
              {t("+ Create a new play")}
            </Button>
          </Link>
        </div>
      </div>

      {!showOverview ? (
        <div className="space-y-4 px-4 py-4">
          <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 sm:flex-row sm:items-end">
            <div className="flex-1">
              <InputField
                label={t("Company website")}
                value={websiteUrl}
                onChange={setWebsiteUrl}
                placeholder="https://example.com"
                disabled={building || isInProgress(siteSource?.status)}
              />
            </div>
            <Button
              type="button"
              variant="primary"
              size="md"
              isLoading={building || isInProgress(siteSource?.status)}
              onClick={onBuild}
            >
              {t("Build")}
            </Button>
          </div>

          {siteSource?.status ? (
            <div
              className={`rounded-lg border px-3 py-2 text-sm ${
                siteSource.status === "error"
                  ? "border-red-200 bg-red-50 text-red-800"
                  : siteSource.status === "suggested"
                    ? "border-green-200 bg-green-50 text-green-800"
                    : "border-blue-200 bg-blue-50 text-blue-800"
              }`}
            >
              <span className="font-medium">{t(statusLabel(siteSource.status))}</span>
              {siteSource.errorMessage ? (
                <span className="ml-2">{siteSource.errorMessage}</span>
              ) : null}
              {latestReview?.verdict && siteSource.status === "suggested" ? (
                <span className="ml-2">
                  · {t("Review")}: {latestReview.verdict}
                </span>
              ) : null}
            </div>
          ) : null}

          {loading ? (
            <p className="text-sm text-gray-500">{t("Loading…")}</p>
          ) : !hasBrain && !isInProgress(siteSource?.status) ? (
            <div className="rounded-lg border border-dashed border-gray-300 px-6 py-10 text-center">
              <p className="text-sm font-medium text-gray-900">{t("No brain yet")}</p>
              <p className="mt-1 text-sm text-gray-500">
                {t("Enter a company https website above and click Build to suggest a GTM brain.")}
              </p>
            </div>
          ) : showEditor ? (
            <GtmBrainEditorPanel
              brain={brain}
              docs={docs}
              openSections={openSections}
              onToggleSection={toggleSection}
              onBrainChange={setBrain}
              onDocsChange={setDocs}
            />
          ) : (
            <p className="text-sm text-gray-500">{t("Building your brain from the website…")}</p>
          )}
        </div>
      ) : (
        <div className="flex min-h-0 flex-1">
          <BrainKnowledgeSidebar
            rows={flow.knowledge}
            docs={docs}
            selectedCategoryId={selectedCategoryId}
            selectedMarketDocId={selectedMarketDocId}
            selectedPersonaKey={selectedPersonaKey}
            selectedPillarKey={selectedPillarKey}
            selectedObjectionKey={selectedObjectionKey}
            selectedCompetitorKey={selectedCompetitorKey}
            selectedInfluencerKey={selectedInfluencerKey}
            selectedVoiceDocId={selectedVoiceDocId}
            selectedSignalDocId={selectedSignalDocId}
            query={searchQuery}
            onQueryChange={setSearchQuery}
            onSelectCategory={selectCategory}
            onSelectMarketDoc={openMarketDoc}
            onSelectPersona={openPersona}
            onSelectPillar={openPillar}
            onSelectObjection={openObjection}
            onSelectCompetitor={openCompetitor}
            onSelectInfluencer={openInfluencer}
            onSelectVoiceDoc={openVoiceDoc}
            onSelectSignalDoc={openSignalDoc}
            onBrainOverview={backToOverview}
          />
          {showMarketBrowse ? (
            <BrainMarketProfilePanel
              brain={brain}
              onOpenDoc={openMarketDoc}
              onBackToOverview={backToOverview}
            />
          ) : null}
          {showMarketDoc && selectedMarketDocId ? (
            <BrainMarketDocView
              brain={brain}
              docId={selectedMarketDocId}
              onBrainChange={setBrain}
              onBackToMarket={backToMarketList}
              onBackToOverview={backToOverview}
            />
          ) : null}
          {showPersonasBrowse ? (
            <BrainPersonasPanel
              docs={docs}
              onOpenPersona={openPersona}
              onAddPersona={addPersona}
              onBackToOverview={backToOverview}
            />
          ) : null}
          {showPersonaDoc && selectedPersonaKey ? (
            <BrainPersonaDocView
              docs={docs}
              personaKey={selectedPersonaKey}
              onDocsChange={setDocs}
              onDiscard={discardPersona}
              onBackToPersonas={backToPersonasList}
              onBackToOverview={backToOverview}
            />
          ) : null}
          {showPillarsBrowse ? (
            <BrainPillarsPanel
              docs={docs}
              onOpenPillar={openPillar}
              onAddPillar={addPillar}
              onBackToOverview={backToOverview}
            />
          ) : null}
          {showPillarDoc && selectedPillarKey ? (
            <BrainPillarDocView
              docs={docs}
              pillarKey={selectedPillarKey}
              onDocsChange={setDocs}
              onDiscard={discardPillar}
              onBackToPillars={backToPillarsList}
              onBackToOverview={backToOverview}
            />
          ) : null}
          {showObjectionsBrowse ? (
            <BrainObjectionsPanel
              docs={docs}
              onOpenObjection={openObjection}
              onAddObjection={addObjection}
              onBackToOverview={backToOverview}
            />
          ) : null}
          {showObjectionDoc && selectedObjectionKey ? (
            <BrainObjectionDocView
              docs={docs}
              objectionKey={selectedObjectionKey}
              onDocsChange={setDocs}
              onDiscard={discardObjection}
              onBackToObjections={backToObjectionsList}
              onBackToOverview={backToOverview}
            />
          ) : null}
          {showCompetitorsBrowse ? (
            <BrainCompetitorsPanel
              docs={docs}
              onOpenCompetitor={openCompetitor}
              onAddCompetitor={addCompetitor}
              onBackToOverview={backToOverview}
            />
          ) : null}
          {showCompetitorDoc && selectedCompetitorKey ? (
            <BrainCompetitorDocView
              docs={docs}
              competitorKey={selectedCompetitorKey}
              onDocsChange={setDocs}
              onDiscard={discardCompetitor}
              onBackToCompetitors={backToCompetitorsList}
              onBackToOverview={backToOverview}
            />
          ) : null}
          {showInfluencersBrowse ? (
            <BrainInfluencersPanel
              docs={docs}
              onOpenInfluencer={openInfluencer}
              onAddInfluencer={addInfluencer}
              onBackToOverview={backToOverview}
            />
          ) : null}
          {showInfluencerDoc && selectedInfluencerKey ? (
            <BrainInfluencerDocView
              docs={docs}
              influencerKey={selectedInfluencerKey}
              onDocsChange={setDocs}
              onDiscard={discardInfluencer}
              onBackToInfluencers={backToInfluencersList}
              onBackToOverview={backToOverview}
            />
          ) : null}
          {showVoiceBrowse ? (
            <BrainVoicePanel
              brain={brain}
              onOpenDoc={openVoiceDoc}
              onBackToOverview={backToOverview}
            />
          ) : null}
          {showVoiceDoc && selectedVoiceDocId ? (
            <BrainVoiceDocView
              brain={brain}
              docId={selectedVoiceDocId}
              onBrainChange={setBrain}
              onBackToVoice={backToVoiceList}
              onBackToOverview={backToOverview}
            />
          ) : null}
          {showSignalsBrowse ? (
            <BrainSignalsPanel
              brain={brain}
              onOpenDoc={openSignalDoc}
              onBackToOverview={backToOverview}
            />
          ) : null}
          {showSignalDoc && selectedSignalDocId ? (
            <BrainSignalDocView
              brain={brain}
              docId={selectedSignalDocId}
              onBackToSignals={backToSignalsList}
              onBackToOverview={backToOverview}
            />
          ) : null}
          {showKeywordsBrowse ? (
            <BrainKeywordsPanel
              docs={docs}
              onDocsChange={setDocs}
              onBackToOverview={backToOverview}
            />
          ) : null}
          <div className="relative min-h-0 min-w-[320px] flex-1 basis-0">
            <BrainOverviewCanvas
              nodes={flow.nodes}
              edges={flow.edges}
              onNodeClick={({ categoryId, marketDocId, voiceDocId, signalDocId, docId }) => {
                if (categoryId === "market") {
                  if (marketDocId) {
                    openMarketDoc(marketDocId);
                  } else {
                    selectCategory("market");
                  }
                  return;
                }
                if (categoryId === "voice") {
                  if (voiceDocId) {
                    openVoiceDoc(voiceDocId);
                  } else {
                    selectCategory("voice");
                  }
                  return;
                }
                if (categoryId === "signals") {
                  if (signalDocId) {
                    openSignalDoc(signalDocId);
                  } else {
                    selectCategory("signals");
                  }
                  return;
                }
                if (categoryId === "keywords") {
                  selectCategory("keywords");
                  return;
                }
                if (categoryId === "personas") {
                  if (docId) {
                    const match = listPersonas(docs).find(
                      (p) => p.key === docId || p.doc.id === docId
                    );
                    openPersona(match?.key || docId);
                  } else {
                    selectCategory("personas");
                  }
                  return;
                }
                if (categoryId === "pillars") {
                  if (docId) {
                    const match = listPillars(docs).find(
                      (p) => p.key === docId || p.doc.id === docId
                    );
                    openPillar(match?.key || docId);
                  } else {
                    selectCategory("pillars");
                  }
                  return;
                }
                if (categoryId === "objections") {
                  if (docId) {
                    const match = listObjections(docs).find(
                      (o) => o.key === docId || o.doc.id === docId
                    );
                    openObjection(match?.key || docId);
                  } else {
                    selectCategory("objections");
                  }
                  return;
                }
                if (categoryId === "competitors") {
                  if (docId) {
                    const match = listCompetitors(docs).find(
                      (c) => c.key === docId || c.doc.id === docId
                    );
                    openCompetitor(match?.key || docId);
                  } else {
                    selectCategory("competitors");
                  }
                  return;
                }
                if (categoryId === "influencers") {
                  if (docId) {
                    const match = listInfluencers(docs).find(
                      (i) => i.key === docId || i.doc.id === docId
                    );
                    openInfluencer(match?.key || docId);
                  } else {
                    selectCategory("influencers");
                  }
                  return;
                }
                openEditor(categoryId);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
